import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Loader2, Square, X } from "lucide-react";
import { LumiAvatar } from "@/components/lumi/LumiAvatar";
import { useLumiVoice } from "@/hooks/useLumiVoice";
import { getCachedLumiUrl } from "@/lib/audio/lumiAudioCache";
import { useSfx } from "@/hooks/useSfx";
import { cn } from "@/lib/utils";
import type { LumiCoachingLine, LumiCoachingType } from "@/data/lumiLaunchCoaching";

interface Props {
  line: LumiCoachingLine;
  /** Optional smaller chip-style render — used for inline placements. */
  variant?: "default" | "compact" | "inline";
  /** Optional dismissible mode (uses localStorage key to remember). */
  dismissKey?: string;
  className?: string;
  /** Optional voice context id (forwarded to ElevenLabs cache scope). */
  contextId?: string | null;
  /**
   * Allow learner-initiated TTS generation on cache miss. Coaching lines
   * are bounded/approved content (from `lumiBetaCoaching`), and the very
   * first play seeds the server cache so subsequent learners hit it.
   * Defaults to `true` so the Decide/coaching surfaces don't go silent
   * when an admin has not pre-seeded audio yet.
   */
  allowGenerate?: boolean;
}

const TONE: Record<LumiCoachingType, string> = {
  encouragement: "border-emerald-400/60 bg-emerald-500/10",
  correction: "border-amber-400/60 bg-amber-500/10",
  risk_warning: "border-rose-400/65 bg-rose-500/10",
  next_step: "border-primary/60 bg-primary/10",
  reflection: "border-violet-400/60 bg-violet-500/10",
};

const TONE_TEXT: Record<LumiCoachingType, string> = {
  encouragement: "text-emerald-200",
  correction: "text-amber-200",
  risk_warning: "text-rose-200",
  next_step: "text-primary",
  reflection: "text-violet-200",
};

function isDismissed(key: string | undefined): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(`lumi-dismiss:${key}`) === "1";
  } catch {
    return false;
  }
}

function setDismissed(key: string | undefined) {
  if (!key) return;
  try {
    localStorage.setItem(`lumi-dismiss:${key}`, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Lumi coaching card — pure presentational. Caches the "appear" SFX per
 * cacheKey so re-renders never re-trigger it. Voice playback is opt-in
 * via the existing `useLumiVoice` hook (which ducks music and dedupes
 * its own audio across cards).
 */
export function LumiCoachingCard({
  line,
  variant = "default",
  dismissKey,
  className,
  contextId = null,
  allowGenerate = true,
}: Props) {
  const { playSfx } = useSfx();
  const { play, stop, status } = useLumiVoice();
  const appearedRef = useRef<string | null>(null);
  const dismissedRef = useRef(false);
  const [unavailable, setUnavailable] = useState(false);

  // Honor dismissal from a prior session.
  if (!dismissedRef.current && isDismissed(dismissKey)) {
    dismissedRef.current = true;
  }

  useEffect(() => {
    if (appearedRef.current === line.cacheKey) return;
    appearedRef.current = line.cacheKey;
    playSfx("soft_swoosh");
  }, [line.cacheKey, playSfx]);

  if (dismissedRef.current) return null;

  const playbackText = line.message;

  const handlePlay = () => {
    if (status === "loading") return;
    if (status === "playing") {
      stop();
      return;
    }
    const cachedUrl = getCachedLumiUrl(playbackText);
    console.log("[Lumi] speaker clicked", {
      cacheKey: line.cacheKey,
      contextId,
      hasCachedUrl: !!cachedUrl,
      voiceId: "KR1TkIhkSykEjI4B0DtH",
    });
    playSfx("click_primary");
    void Promise.resolve(play(playbackText, { contextId, allowGenerate })).then(() => {
      // If after attempted play the URL still isn't cached, mark unavailable.
      setTimeout(() => {
        if (!getCachedLumiUrl(playbackText)) {
          console.warn("[Lumi] audio unavailable — needs admin seeding", {
            cacheKey: line.cacheKey,
            voiceId: "KR1TkIhkSykEjI4B0DtH",
          });
          setUnavailable(true);
        }
      }, 1200);
    });
  };

  const handleDismiss = () => {
    setDismissed(dismissKey);
    dismissedRef.current = true;
    // Force re-render
    appearedRef.current = `${line.cacheKey}:dismissed`;
  };

  const padded =
    variant === "compact"
      ? "p-3 gap-2"
      : variant === "inline"
      ? "p-3 sm:p-3.5 gap-2.5"
      : "p-4 sm:p-5 gap-3";

  return (
    <div
      className={cn(
        "relative flex items-start rounded-2xl border-2 bg-background/70 backdrop-blur-xl shadow-[0_0_28px_-10px_hsl(var(--primary)/0.55)]",
        TONE[line.coachingType],
        padded,
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <LumiAvatar size={variant === "compact" ? "sm" : "md"} className="mt-0.5" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-foreground">
            Lumi
          </span>
          <span
            className="text-[10px] font-heading font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-white/15 border border-white/30 text-white"
          >
            {line.coachingType.replace("_", " ")}
          </span>
        </div>
        <div className="font-heading font-semibold text-foreground text-sm sm:text-[15px] leading-snug">
          {line.title}
        </div>
        <p className="font-body text-[13px] sm:text-sm leading-relaxed text-foreground/95">
          {line.message}
        </p>
        {line.suggestedAction ? (
          <p className="font-body text-xs text-foreground/85 pt-1">
            <span className="font-semibold text-foreground">Try this: </span>
            {line.suggestedAction}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center self-stretch pr-1">
        <button
          type="button"
          onClick={handlePlay}
          className={cn(
            "group inline-flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-50",
            status === "playing"
              ? "border-primary bg-primary text-primary-foreground shadow-[0_0_18px_-2px_hsl(var(--primary)/0.8)] animate-pulse"
              : "border-white/80 bg-primary/30 text-white hover:bg-primary hover:text-white hover:shadow-[0_0_18px_-2px_hsl(var(--primary)/0.7)]",
            unavailable && "opacity-50 cursor-not-allowed",
          )}
          title={
            unavailable
              ? "Audio unavailable"
              : status === "playing"
              ? "Stop"
              : status === "loading"
              ? "Loading…"
              : "Hear Lumi"
          }
          aria-label={
            unavailable
              ? "Lumi audio unavailable"
              : status === "playing"
              ? "Stop Lumi voice"
              : status === "loading"
              ? "Loading Lumi voice"
              : "Hear from Lumi"
          }
          disabled={status === "loading" || unavailable}
        >
          {unavailable ? (
            <VolumeX className="h-4 w-4" />
          ) : status === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : status === "playing" ? (
            <Square className="h-3.5 w-3.5 fill-current" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
        </button>
      </div>
      {dismissKey ? (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-foreground/25 bg-background/60 text-foreground/80 transition-colors hover:bg-foreground/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          aria-label="Dismiss Lumi tip"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      ) : null}
    </div>
  );
}

export default LumiCoachingCard;